import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  ArrowUpward,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PaperSection from '../../components/PaperSection';
import ContentWrapper from '../../layout/ContentWrapper';
import { Organisation, UserRole } from '@fsp/types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getOrganisationProfile, updateOrganisationProfile } from '../../services';
import { useOrganisationProfile } from '../../hooks/useOrganisationProfile';

export const OrganisationRoles = [UserRole.fsp, UserRole.OWNER, UserRole.ADMIN];
interface OrganisationFormData {
  name: string;
  legalName: string;
  abn: string;
  contactNumber: string;
  website: string;
  billingEmail: string;
  billingAddress: string;
}

// Validation schema
const schema = yup.object({
  name: yup.string().required('Organisation name is required'),
  legalName: yup.string().default(''),
  abn: yup
    .string()
    .default('')
    .test('abn-format', 'ABN must be 11 digits', function (value: string | undefined) {
      if (!value || !value.trim()) return true; // Optional field
      const cleanABN = value.replace(/\s/g, '');
      return /^\d{11}$/.test(cleanABN);
    }),
  contactNumber: yup.string().default(''),
  website: yup
    .string()
    .default('')
    .test(
      'url-format',
      'Website must start with http:// or https://',
      function (value: string | undefined) {
        if (!value || !value.trim()) return true; // Optional field
        return /^https?:\/\/.+/.test(value);
      }
    ),
  billingEmail: yup
    .string()
    .required('Billing email is required')
    .email('Please enter a valid email'),
  billingAddress: yup.string().default(''),
});

const OrganisationComponent: React.FC = () => {
  const { organisationId, refreshUserOrganisationState } = useAuth();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const [originalData, setOriginalData] = useState<Organisation | null>(null);

  // Fetch AI-generated organisation profile
  const {
    profile: aiProfile,
    loading: aiProfileLoading,
    error: aiProfileError,
  } = useOrganisationProfile(organisationId);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<OrganisationFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      legalName: '',
      abn: '',
      contactNumber: '',
      website: '',
      billingEmail: '',
      billingAddress: '',
    },
  });

  // Watch all form values to detect changes
  const formData = watch();

  useEffect(() => {
    fetchOrganisationData();
  }, []);

  const fetchOrganisationData: () => Promise<Organisation | void> = async () => {
    setLoading(true);
    try {
      if (!organisationId) {
        navigate('/onboarding/organisation', { replace: true });
        return;
      }
      const organisation = await getOrganisationProfile(organisationId);
      if (!organisation) {
        navigate('/onboarding/organisation', { replace: true });
        return;
      }

      setOriginalData(organisation);

      // Set form values with formatted data
      setValue('name', organisation.name || '');
      setValue('legalName', organisation.legalName || '');
      setValue('abn', organisation.abn ? formatABN(organisation.abn) : '');
      setValue('contactNumber', organisation.contactNumber || '');
      setValue('website', organisation.website || '');
      setValue('billingEmail', organisation.billingEmail || '');
      setValue('billingAddress', organisation.billingAddress || '');

      // Reset form dirty state after setting initial values
      reset({
        name: organisation.name || '',
        legalName: organisation.legalName || '',
        abn: organisation.abn ? formatABN(organisation.abn) : '',
        contactNumber: organisation.contactNumber || '',
        website: organisation.website || '',
        billingEmail: organisation.billingEmail || '',
        billingAddress: organisation.billingAddress || '',
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      enqueueSnackbar('Failed to load organisation details', { variant: 'error' });
    }
  };

  const onSubmit = async (formData: OrganisationFormData) => {
    if (!organisationId) {
      enqueueSnackbar('Organisation ID is required', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Clean up the ABN by removing spaces for storage and prepare update payload
      const updatePayload = {
        ...formData,
        abn: formData.abn.replace(/\s/g, ''),
        website: formData.website || undefined,
      };

      const result = await updateOrganisationProfile(organisationId, updatePayload);
      refreshUserOrganisationState();

      // Update original data with the response
      if (result.organisation) {
        setOriginalData(result.organisation);

        // Reset form with new data to clear dirty state
        reset({
          name: result.organisation.name || '',
          legalName: result.organisation.legalName || '',
          abn: result.organisation.abn ? formatABN(result.organisation.abn) : '',
          contactNumber: result.organisation.contactNumber || '',
          website: result.organisation.website || '',
          billingEmail: result.organisation.billingEmail || '',
          billingAddress: result.organisation.billingAddress || '',
        });
      }

      setSaving(false);
      enqueueSnackbar('Organisation details updated successfully!', { variant: 'success' });
    } catch (error) {
      setSaving(false);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update organisation details';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('Error saving organisation:', error);
    }
  };

  const formatABN = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XX XXX XXX XXX
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    enqueueSnackbar('Organisation ID copied to clipboard', { variant: 'info' });
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Never';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCancel = () => {
    if (originalData) {
      // Reset form to original data
      reset({
        name: originalData.name || '',
        legalName: originalData.legalName || '',
        abn: originalData.abn ? formatABN(originalData.abn) : '',
        contactNumber: originalData.contactNumber || '',
        website: originalData.website || '',
        billingEmail: originalData.billingEmail || '',
        billingAddress: originalData.billingAddress || '',
      });
    }
  };

  const handleClose = () => {
    navigate('/settings');
  };

  const headerActions = (
    <IconButton onClick={handleClose}>
      <ArrowUpward />
    </IconButton>
  );

  if (loading) {
    return (
      <ContentWrapper breadcrumb>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </ContentWrapper>
    );
  }

  if (!originalData) {
    return (
      <ContentWrapper breadcrumb>
        <Alert severity="error">Failed to load organisation data</Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper breadcrumb>
      <PaperSection
        title="Organisation Details"
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {formatDate(originalData.updatedAt)}
            </Typography>
            {headerActions}
          </Stack>
        }
      >
        {/* Header */}
        <Box mb={4}>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Organisation ID:
              </Typography>
              <Chip
                label={originalData.id}
                size="small"
                variant="outlined"
                onDelete={undefined}
                sx={{ backgroundColor: '#fff' }}
                deleteIcon={
                  <Tooltip title={copied ? 'Copied!' : 'Copy ID'}>
                    <IconButton size="small" onClick={() => copyToClipboard(originalData.id || '')}>
                      {copied ? (
                        <CheckCircleIcon fontSize="small" />
                      ) : (
                        <CopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                }
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Subscription:
              </Typography>
              <Chip
                label={originalData.subscriptionStatus}
                size="small"
                color={originalData.subscriptionStatus === 'ACTIVE' ? 'success' : 'default'}
              />
              {originalData.subscriptionValidUntil && (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="caption" color="text.secondary">
                    Valid Until:
                  </Typography>
                  <Typography variant="caption" color="text.primary">
                    {formatDate(originalData.subscriptionValidUntil)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={4} maxWidth={700}>
            {/* Company Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Company Information
              </Typography>
              <Stack spacing={3}>
                <Controller
                  name="name"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'name'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Organisation Name"
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />

                <Controller
                  name="legalName"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'legalName'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Legal Name"
                      variant="outlined"
                      error={!!errors.legalName}
                      helperText={errors.legalName?.message || 'As registered with ASIC'}
                    />
                  )}
                />

                <Controller
                  name="abn"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'abn'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="ABN"
                      variant="outlined"
                      error={!!errors.abn}
                      helperText={errors.abn?.message || 'Australian Business Number (11 digits)'}
                      inputProps={{ maxLength: 14 }} // 11 digits + 3 spaces
                      onChange={(e) => {
                        const formatted = formatABN(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                  )}
                />

                <Controller
                  name="website"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'website'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Website"
                      variant="outlined"
                      placeholder="https://example.com.au"
                      error={!!errors.website}
                      helperText={errors.website?.message}
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Contact Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Contact Information
              </Typography>
              <Stack spacing={3}>
                <Controller
                  name="contactNumber"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'contactNumber'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      error={!!errors.contactNumber}
                      helperText={errors.contactNumber?.message}
                    />
                  )}
                />

                <Controller
                  name="billingEmail"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<OrganisationFormData, 'billingEmail'>;
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Billing Email"
                      type="email"
                      variant="outlined"
                      error={!!errors.billingEmail}
                      helperText={
                        errors.billingEmail?.message ||
                        "Where we'll send invoices and billing notifications"
                      }
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Billing Information */}
            <Box>
              <Typography variant="h6" fontWeight={500} mb={3}>
                Billing Information
              </Typography>
              <Controller
                name="billingAddress"
                control={control}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<OrganisationFormData, 'billingAddress'>;
                }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Billing Address"
                    variant="outlined"
                    error={!!errors.billingAddress}
                    helperText={
                      errors.billingAddress?.message || 'Your registered business address'
                    }
                  />
                )}
              />
            </Box>

            <Divider />

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {isDirty && (
                <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || !isDirty}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </PaperSection>

      {/* Temporary section to show AI-generated company profile */}
      <Box mt={3}>
        <PaperSection title="AI-Generated Company Profile (Temporary)">
          {aiProfileLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {aiProfileError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {aiProfileError}
            </Alert>
          )}

          {!aiProfileLoading && !aiProfile && (
            <Alert severity="info">
              No AI-generated company profile found for this organisation.
            </Alert>
          )}

          {!aiProfileLoading && aiProfile && aiProfile.profileData && (
            <Stack spacing={3}>
              {/* Business Info */}
              {aiProfile.profileData.businessInfo && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Business Information
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Legal Name:
                        </Typography>
                        <Typography variant="body2">
                          {aiProfile.profileData.businessInfo.legalName || 'N/A'}
                        </Typography>
                      </Box>
                      {aiProfile.profileData.businessInfo.tradingName && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Trading Name:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.businessInfo.tradingName}
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Business Type:
                        </Typography>
                        <Typography variant="body2">
                          {aiProfile.profileData.businessInfo.businessType || 'N/A'}
                        </Typography>
                      </Box>
                      {aiProfile.profileData.businessInfo.yearEstablished && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Year Established:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.businessInfo.yearEstablished}
                          </Typography>
                        </Box>
                      )}
                      {aiProfile.profileData.businessInfo.numberOfEmployees && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Number of Employees:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.businessInfo.numberOfEmployees}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Location */}
              {aiProfile.profileData.location && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Location
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Country:
                        </Typography>
                        <Typography variant="body2">
                          {aiProfile.profileData.location.country || 'N/A'}
                        </Typography>
                      </Box>
                      {aiProfile.profileData.location.state && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            State:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.location.state}
                          </Typography>
                        </Box>
                      )}
                      {aiProfile.profileData.location.city && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            City:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.location.city}
                          </Typography>
                        </Box>
                      )}
                      {aiProfile.profileData.location.address && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Address:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.location.address}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Facility */}
              {aiProfile.profileData.facility && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Facility
                    </Typography>
                    <Stack spacing={1.5}>
                      {aiProfile.profileData.facility.totalArea && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Area:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.facility.totalArea}
                          </Typography>
                        </Box>
                      )}
                      {aiProfile.profileData.facility.productionArea && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Production Area:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.facility.productionArea}
                          </Typography>
                        </Box>
                      )}
                      {aiProfile.profileData.facility.storageCapabilities &&
                        aiProfile.profileData.facility.storageCapabilities.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Storage Capabilities:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {aiProfile.profileData.facility.storageCapabilities.map(
                                (cap, idx) => (
                                  <Chip key={idx} label={cap} size="small" />
                                )
                              )}
                            </Stack>
                          </Box>
                        )}
                      {aiProfile.profileData.facility.waterSource && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Water Source:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.facility.waterSource}
                          </Typography>
                        </Box>
                      )}
                      {aiProfile.profileData.facility.wastewaterTreatment && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Wastewater Treatment:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.facility.wastewaterTreatment}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Regulatory */}
              {aiProfile.profileData.regulatory && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Regulatory
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Country:
                        </Typography>
                        <Typography variant="body2">
                          {aiProfile.profileData.regulatory.country || 'N/A'}
                        </Typography>
                      </Box>
                      {aiProfile.profileData.regulatory.primaryRegulator && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Primary Regulator:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.regulatory.primaryRegulator}
                          </Typography>
                        </Box>
                      )}
                      {aiProfile.profileData.regulatory.applicableStandards &&
                        aiProfile.profileData.regulatory.applicableStandards.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Applicable Standards:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {aiProfile.profileData.regulatory.applicableStandards.map(
                                (standard, idx) => (
                                  <Chip key={idx} label={standard} size="small" />
                                )
                              )}
                            </Stack>
                          </Box>
                        )}
                      {aiProfile.profileData.regulatory.certifications &&
                        aiProfile.profileData.regulatory.certifications.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Certifications:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {aiProfile.profileData.regulatory.certifications.map((cert, idx) => (
                                <Chip key={idx} label={cert} size="small" color="success" />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      {aiProfile.profileData.regulatory.exportMarkets &&
                        aiProfile.profileData.regulatory.exportMarkets.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Export Markets:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {aiProfile.profileData.regulatory.exportMarkets.map((market, idx) => (
                                <Chip key={idx} label={market} size="small" />
                              ))}
                            </Stack>
                          </Box>
                        )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Infrastructure (if present) */}
              {aiProfile.profileData.infrastructure && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Infrastructure
                    </Typography>
                    <Stack spacing={1.5}>
                      {aiProfile.profileData.infrastructure.equipmentTypes &&
                        aiProfile.profileData.infrastructure.equipmentTypes.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Equipment Types:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {aiProfile.profileData.infrastructure.equipmentTypes.map(
                                (equip, idx) => (
                                  <Chip key={idx} label={equip} size="small" />
                                )
                              )}
                            </Stack>
                          </Box>
                        )}
                      {aiProfile.profileData.infrastructure.utilities &&
                        aiProfile.profileData.infrastructure.utilities.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Utilities:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {aiProfile.profileData.infrastructure.utilities.map((util, idx) => (
                                <Chip key={idx} label={util} size="small" />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      {aiProfile.profileData.infrastructure.ventilation && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Ventilation:
                          </Typography>
                          <Typography variant="body2">
                            {aiProfile.profileData.infrastructure.ventilation}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Raw JSON for debugging */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Raw Profile Data
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: '0.75rem',
                    }}
                  >
                    {JSON.stringify(aiProfile.profileData, null, 2)}
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          )}
        </PaperSection>
      </Box>
    </ContentWrapper>
  );
};

export default OrganisationComponent;
